import { z } from 'zod';
import { createRpcContract, createRpcCaller } from '@/lib/rpc';

// ============================================
// TYPES
// ============================================

export interface Review {
  id: string;
  programId: string;
  programName: string;
  userId: string;
  userEmail: string | null;
  userDisplayName: string | null;
  rating: number;
  reviewText: string | null;
  createdAt: Date;
}

// ============================================
// RPC CONTRACTS
// ============================================

// Get all reviews across programs
export const getAllReviewsContract = createRpcContract(
  'admin_get_all_reviews',
  z.object({
    p_limit: z.number().optional(),
    p_offset: z.number().optional(),
    p_search: z.string().optional(),
  }),
  z.array(
    z.object({
      id: z.string(),
      program_id: z.string(),
      program_name: z.string(),
      user_id: z.string(),
      user_email: z.string().nullable(),
      user_display_name: z.string().nullable(),
      rating: z.number(),
      review_text: z.string().nullable(),
      created_at: z.string(),
    })
  )
);

// Get reviews for a specific program
export const getProgramReviewsContract = createRpcContract(
  'admin_get_program_reviews',
  z.object({
    p_program_id: z.string().uuid(),
    p_limit: z.number().optional(),
    p_offset: z.number().optional(),
  }),
  z.array(
    z.object({
      id: z.string(),
      user_id: z.string(),
      user_email: z.string().nullable(),
      user_display_name: z.string().nullable(),
      rating: z.number(),
      review_text: z.string().nullable(),
      created_at: z.string(),
      updated_at: z.string().nullable(),
    })
  )
);

// Delete a review
export const deleteReviewContract = createRpcContract(
  'admin_delete_review',
  z.object({
    p_review_id: z.string().uuid(),
  }),
  z.void()
);

// ============================================
// RPC CALLERS
// ============================================

export const moderationRpc = {
  getAllReviews: createRpcCaller(getAllReviewsContract),
  getProgramReviews: createRpcCaller(getProgramReviewsContract),
  deleteReview: createRpcCaller(deleteReviewContract),
};

// ============================================
// TRANSFORM HELPERS
// ============================================

export function transformReview(
  raw: z.infer<typeof getAllReviewsContract.outputSchema>[number]
): Review {
  return {
    id: raw.id,
    programId: raw.program_id,
    programName: raw.program_name,
    userId: raw.user_id,
    userEmail: raw.user_email,
    userDisplayName: raw.user_display_name,
    rating: raw.rating,
    reviewText: raw.review_text,
    createdAt: new Date(raw.created_at),
  };
}
